import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Zap, Trophy, ArrowLeft, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AuthModal from "@/components/AuthModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

const TIERS = {
  free: {
    name: "Free",
    price: "$0",
    period: "forever",
    icon: Sparkles,
    description: "Get started with basic hockey stats",
    features: [
      { text: "View league standings", included: true },
      { text: "View season statistics", included: true },
      { text: "Basic game schedules", included: true },
      { text: "Track up to 3 players", included: true },
      { text: "Personalized dashboard", included: false },
      { text: "Manage favorite teams", included: false },
      { text: "Live betting odds", included: false },
      { text: "Priority alerts", included: false },
    ],
    cta: "Current Plan",
    disabled: true,
  },
  light: {
    name: "Light",
    price: "$1.69",
    period: "/month",
    trialText: "3 months free trial",
    icon: Zap,
    description: "Perfect for casual hockey fans",
    features: [
      { text: "View league standings", included: true },
      { text: "View season statistics", included: true },
      { text: "Full game schedules", included: true },
      { text: "Track up to 8 players", included: true },
      { text: "Personalized dashboard", included: true },
      { text: "Manage favorite teams", included: true },
      { text: "Live betting odds", included: false },
      { text: "Priority alerts", included: false },
    ],
    cta: "Start Free Trial",
  },
  pro: {
    name: "Pro",
    price: "$2.79",
    period: "/month",
    trialText: "1 month free trial",
    icon: Crown,
    description: "For dedicated hockey enthusiasts",
    features: [
      { text: "View league standings", included: true },
      { text: "View season statistics", included: true },
      { text: "Full game schedules", included: true },
      { text: "Track up to 8 players", included: true },
      { text: "Personalized dashboard", included: true },
      { text: "Manage favorite teams", included: true },
      { text: "Player career stats", included: true },
      { text: "Priority alerts", included: false },
    ],
    cta: "Start Free Trial",
  },
  elite: {
    name: "Elite",
    price: "$3.49",
    period: "/month",
    trialText: "1 month free trial",
    icon: Trophy,
    description: "Ultimate hockey experience",
    features: [
      { text: "View league standings", included: true },
      { text: "View season statistics", included: true },
      { text: "Full game schedules", included: true },
      { text: "Track up to 24 players", included: true },
      { text: "Personalized dashboard", included: true },
      { text: "Manage favorite teams", included: true },
      { text: "Player career stats", included: true },
      { text: "Live betting odds", included: true },
      { text: "Priority alerts", included: true },
      { text: "Export reports", included: true },
      { text: "Early access to features", included: true },
    ],
    popular: true,
    cta: "Start Free Trial",
  },
};

export default function Pricing() {
  const navigate = useNavigate();
  const { session, subscription } = useAuth();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleSubscribe = async (tier: "light" | "pro" | "elite") => {
    if (!session) {
      setShowAuthModal(true);
      return;
    }

    setIsLoading(tier);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { tier },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast.error(error.message || "Failed to start checkout");
    } finally {
      setIsLoading(null);
    }
  };

  const isCurrentTier = (tierKey: string) => {
    if (!subscription.subscribed && tierKey === "free") return true;
    return subscription.tier === tierKey;
  };

  return (
    <div className="min-h-screen bg-background pt-16">
      <Navbar />
      <main className="container mx-auto px-6 py-12">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8 gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Unlock premium hockey statistics, personalized dashboards, and priority alerts. 
            Start with a free trial and upgrade anytime.
          </p>
        </div>

        {/* Trial Notice */}
        <div className="max-w-2xl mx-auto mb-12 p-4 bg-primary/10 rounded-xl border border-primary/20 text-center">
          <p className="text-sm text-foreground">
            <span className="font-semibold">🎉 Special Offer:</span> Light & Pro subscribers get{" "}
            <span className="text-primary font-semibold">full Elite access</span> for the first 5 days of their trial!
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {(Object.entries(TIERS) as [string, typeof TIERS[keyof typeof TIERS]][]).map(
            ([key, tier]) => {
              const Icon = tier.icon;
              const isPopular = "popular" in tier && tier.popular;
              const isCurrent = isCurrentTier(key);
              const isFreeTier = key === "free";

              return (
                <Card
                  key={key}
                  className={cn(
                    "relative transition-all hover:shadow-lg",
                    isPopular && "border-primary bg-primary/5 scale-105",
                    isCurrent && "ring-2 ring-primary"
                  )}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full whitespace-nowrap">
                      Most Popular
                    </div>
                  )}
                  {isCurrent && !isFreeTier && (
                    <div className="absolute -top-3 right-4 px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                      Current
                    </div>
                  )}

                  <CardHeader className="text-center pb-4">
                    <div
                      className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4",
                        isPopular ? "bg-primary text-primary-foreground" : "bg-muted"
                      )}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-display font-bold text-xl">{tier.name}</h3>
                    {"trialText" in tier && (
                      <p className="text-xs text-primary font-medium">{tier.trialText}</p>
                    )}
                    <p className="text-sm text-muted-foreground mt-1">{tier.description}</p>
                  </CardHeader>

                  <CardContent>
                    <div className="text-center mb-6">
                      <span className="text-4xl font-bold">{tier.price}</span>
                      <span className="text-muted-foreground">{tier.period}</span>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {tier.features.map((feature) => (
                        <li
                          key={feature.text}
                          className={cn(
                            "flex items-center gap-2 text-sm",
                            !feature.included && "text-muted-foreground"
                          )}
                        >
                          <Check
                            className={cn(
                              "w-4 h-4 flex-shrink-0",
                              feature.included ? "text-primary" : "text-muted-foreground"
                            )}
                          />
                          <span className={!feature.included ? "line-through" : ""}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {isFreeTier ? (
                      <Button
                        className="w-full"
                        variant="outline"
                        disabled
                      >
                        {isCurrent ? "Current Plan" : "Free Plan"}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleSubscribe(key as "light" | "pro" | "elite")}
                        disabled={isLoading === key || isCurrent}
                        className="w-full"
                        variant={isPopular ? "default" : "outline"}
                      >
                        {isLoading === key
                          ? "Loading..."
                          : isCurrent
                          ? "Current Plan"
                          : tier.cta}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            }
          )}
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mt-16">
          <h2 className="font-display text-2xl font-bold text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-secondary/50 rounded-xl">
              <h3 className="font-semibold mb-2">Can I cancel anytime?</h3>
              <p className="text-sm text-muted-foreground">
                Yes! You can cancel your subscription at any time from your account settings.
                You'll keep access until the end of your billing period.
              </p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-xl">
              <h3 className="font-semibold mb-2">What happens after the free trial?</h3>
              <p className="text-sm text-muted-foreground">
                After your trial ends, you'll be charged the monthly rate for your selected tier.
                You can upgrade, downgrade, or cancel before the trial ends.
              </p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-xl">
              <h3 className="font-semibold mb-2">What's the "Elite access" during trial?</h3>
              <p className="text-sm text-muted-foreground">
                Light and Pro subscribers get full Elite features (including betting odds) for the first 5 days,
                then revert to their tier-specific features for the remaining trial period.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}
